/**
 * Maps internal class codes (CMT4, PST5, SW6 …) to human-readable labels
 * used on official printed sheets and reports.
 *
 * Convention:
 *  - First 2-3 letters = program (CMT, PST, SW)
 *  - Trailing digit   = NTA level (4 = Basic Technician Certificate,
 *                                   5 = Technician Certificate,
 *                                   6 = Ordinary Diploma)
 */
const PROGRAMS: Record<string, string> = {
  CMT: "Clinical Medicine",
  PST: "Pharmaceutical Sciences",
  SW: "Social Work",
};

const LEVELS: Record<string, string> = {
  "4": "Basic Technician Certificate",
  "5": "Technician Certificate",
  "6": "Ordinary Diploma",
};

function parseClass(code: string | null | undefined): { prog: string; level: string } | null {
  if (!code) return null;
  const m = String(code).trim().toUpperCase().match(/^([A-Z]+)\s*(\d)$/);
  if (!m) return null;
  return { prog: m[1], level: m[2] };
}

export function getProgramFromClass(code: string | null | undefined): string {
  const p = parseClass(code);
  return p ? PROGRAMS[p.prog] ?? p.prog : "";
}

export function getLevelFromClass(code: string | null | undefined): string {
  const p = parseClass(code);
  return p ? LEVELS[p.level] ?? `Level ${p.level}` : "";
}

/**
 * Full display label, e.g. "CMT 4 — Clinical Medicine, Basic Technician Certificate".
 * Falls back to the raw code if it doesn't match the expected pattern.
 */
export function formatClassLabel(code: string | null | undefined): string {
  if (!code) return "—";
  const p = parseClass(code);
  if (!p) return code;
  const program = PROGRAMS[p.prog] ?? p.prog;
  const level = LEVELS[p.level] ?? `Level ${p.level}`;
  return `${p.prog} ${p.level} — ${program}, ${level}`;
}
